"use client";

import { useLang } from "@/contexts/language-context";
import { AnimatePresence, motion } from "framer-motion";
import { Clock3, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

type TableState = "open" | "seated" | "check" | "paying" | "dirty";

interface T {
  id: number;
  x: number;
  y: number;
  seats: number;
  shape?: "round" | "rect";
}

const TABLES: T[] = [
  { id: 1, x: 10, y: 14, seats: 2, shape: "round" },
  { id: 2, x: 28, y: 18, seats: 4 },
  { id: 3, x: 52, y: 14, seats: 4 },
  { id: 4, x: 74, y: 18, seats: 6 },
  { id: 5, x: 90, y: 14, seats: 2, shape: "round" },
  { id: 6, x: 14, y: 44, seats: 4 },
  { id: 7, x: 38, y: 48, seats: 6 },
  { id: 8, x: 62, y: 44, seats: 4 },
  { id: 9, x: 86, y: 48, seats: 2, shape: "round" },
  { id: 10, x: 12, y: 78, seats: 4 },
  { id: 11, x: 34, y: 82, seats: 4 },
  { id: 12, x: 58, y: 78, seats: 6 },
  { id: 13, x: 82, y: 82, seats: 4 },
];

// Choreographed states across the section's animation cycle
type Frame = { state: Record<number, TableState>; clock: string; caption: { en: string; es: string } };

const FRAMES: Frame[] = [
  {
    clock: "6:48 PM",
    caption: { en: "Doors just opened", es: "Acaban de abrir" },
    state: {
      1: "open",
      2: "seated",
      3: "open",
      4: "open",
      5: "open",
      6: "seated",
      7: "open",
      8: "open",
      9: "open",
      10: "open",
      11: "open",
      12: "open",
      13: "open",
    },
  },
  {
    clock: "7:30 PM",
    caption: { en: "Reservation lands at table 7", es: "Reserva llega a la mesa 7" },
    state: {
      1: "seated",
      2: "seated",
      3: "seated",
      4: "open",
      5: "seated",
      6: "check",
      7: "seated",
      8: "seated",
      9: "open",
      10: "seated",
      11: "open",
      12: "seated",
      13: "open",
    },
  },
  {
    clock: "8:14 PM",
    caption: { en: "Saturday rush — full house", es: "Sábado en plena cresta" },
    state: {
      1: "paying",
      2: "seated",
      3: "check",
      4: "seated",
      5: "dirty",
      6: "seated",
      7: "seated",
      8: "paying",
      9: "seated",
      10: "check",
      11: "seated",
      12: "seated",
      13: "seated",
    },
  },
];

const STATE_STYLES: Record<TableState, { bg: string; ring: string; text: string }> = {
  open: { bg: "bg-white", ring: "border-redy-charcoal/15", text: "text-redy-charcoal" },
  seated: { bg: "bg-redy-orange/15", ring: "border-redy-orange/40", text: "text-redy-charcoal" },
  check: { bg: "bg-redy-charcoal", ring: "border-redy-charcoal", text: "text-white" },
  paying: { bg: "bg-redy-red/15", ring: "border-redy-red/45", text: "text-redy-charcoal" },
  dirty: { bg: "bg-black/10", ring: "border-black/20", text: "text-redy-charcoal/50" },
};

export default function RestaurantsFloorMap() {
  const { t, lang } = useLang();
  const [frameIdx, setFrameIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setFrameIdx((i) => (i + 1) % FRAMES.length), 3200);
    return () => clearInterval(id);
  }, []);

  const frame = FRAMES[frameIdx];

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-[1140px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-redy-orange mb-4">
            {t("The floor map", "El mapa del salón")}
          </div>
          <h2 className="text-[40px] md:text-[60px] font-black leading-[1.02] tracking-tight text-redy-charcoal mb-4">
            {t("Your dining room,", "Tu comedor,")}
            <br />
            <span className="redy-text-gradient">{t("mapped.", "mapeado.")}</span>
          </h2>
          <p className="text-lg text-redy-secondary max-w-[600px] mx-auto font-medium">
            {t(
              "Drag tables to match your real floor. Every state — open, seated, check-dropped, paying, dirty — updates live across every device. No more whiteboard. No more shouting across the room.",
              "Arrastra mesas a la disposición real. Cada estado — libre, sentado, cuenta, pagando, sucio — se actualiza en vivo en todos los dispositivos. Sin pizarrón. Sin gritar por el salón."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative bg-[#FBF9F4] border border-black/[0.06] rounded-[28px] p-6 md:p-10 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.15)]"
        >
          {/* Time badge */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="inline-flex items-center gap-2 bg-white border border-black/5 px-3 py-1.5 rounded-full text-xs font-bold text-redy-charcoal shadow-sm">
                <Clock3 className="w-3.5 h-3.5 text-redy-orange" />
                <motion.span key={frame.clock} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  {frame.clock}
                </motion.span>
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={frameIdx}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.25 }}
                  className="text-sm font-bold text-redy-charcoal"
                >
                  {frame.caption[lang as "en" | "es"] ?? frame.caption.en}
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="hidden md:flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-redy-charcoal/50">
              <Sparkles className="w-3 h-3 text-redy-orange" />
              {t("Live · all devices", "En vivo · todos los devices")}
            </div>
          </div>

          {/* Map */}
          <div className="relative aspect-[16/8] bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
            {/* Subtle grid */}
            <div
              className="absolute inset-0 opacity-[0.05]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(0,0,0,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.5) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            />
            {/* Bar at top */}
            <div className="absolute top-2 left-2 right-2 h-2 bg-redy-charcoal/15 rounded-full" />
            <div className="absolute top-5 left-3 text-[9px] font-bold text-redy-charcoal/40 uppercase tracking-wider">
              {t("Bar", "Bar")}
            </div>

            {TABLES.map((tbl) => {
              const state = frame.state[tbl.id] ?? "open";
              const style = STATE_STYLES[state];
              const isRound = tbl.shape === "round";
              return (
                <div
                  key={tbl.id}
                  style={{ left: `${tbl.x}%`, top: `${tbl.y}%` }}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                >
                  <motion.div
                    layout
                    animate={{
                      backgroundColor:
                        state === "check"
                          ? "#1A1A1A"
                          : state === "seated"
                          ? "rgba(244,114,43,0.15)"
                          : state === "paying"
                          ? "rgba(215,38,61,0.15)"
                          : state === "dirty"
                          ? "rgba(0,0,0,0.10)"
                          : "#ffffff",
                    }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className={`border-2 ${style.ring} shadow-[0_2px_8px_rgba(0,0,0,0.06)] flex flex-col items-center justify-center font-mono text-xs font-black ${
                      isRound ? "w-12 h-12 rounded-full" : "w-16 h-12 rounded-xl"
                    } ${style.text}`}
                  >
                    {tbl.id}
                    <span className="text-[8px] font-bold opacity-60 leading-none mt-0.5">
                      {tbl.seats}p
                    </span>
                  </motion.div>
                </div>
              );
            })}
          </div>

          {/* Legend + KPI strip */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
            {(Object.keys(STATE_STYLES) as TableState[]).map((s) => {
              const labels: Record<TableState, { en: string; es: string }> = {
                open: { en: "Open", es: "Libre" },
                seated: { en: "Seated", es: "Sentados" },
                check: { en: "Check dropped", es: "Cuenta entregada" },
                paying: { en: "Paying", es: "Pagando" },
                dirty: { en: "Dirty / turning", es: "Sucio / girando" },
              };
              const sty = STATE_STYLES[s];
              return (
                <div
                  key={s}
                  className="flex items-center gap-2 bg-white border border-black/[0.05] rounded-xl px-3 py-2.5"
                >
                  <span className={`w-4 h-4 rounded ${sty.bg} border ${sty.ring}`} />
                  <span className="text-xs font-bold text-redy-charcoal">
                    {labels[s][lang as "en" | "es"] ?? labels[s].en}
                  </span>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
